import { skipToken } from "@reduxjs/toolkit/query";
import {
  type Automation,
  automationIdSchema,
  AutomationTemplateIds,
  getAutomationPlaybookIds,
} from "@repo/app-types/entities/automation";
import { renderTeammateDisplayName } from "@repo/app-types/entities/teammate";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { type FC, useCallback, useRef, useState } from "react";
import {
  Navigate,
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";
import {
  useAutomationByIdQuery,
  useDeleteAutomationMutation,
  useUpdateAutomationMutation,
} from "../../api/entities/automation.js";
import { useCreatePreviewAutomationRunMutation } from "../../api/entities/automationRun.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { appPaths } from "../../app/appPaths.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import { RouteTabs } from "../../components/layout/routeTabs.js";
import { AutomationDetailActions } from "../../features/automations/automationDetailActions.js";
import { AutomationTemplateDescription } from "../../features/automations/automationTemplateDescription.js";
import { DeleteAutomationModal } from "../../features/automations/deleteAutomationModal.js";
import { useAutomationFieldsContext } from "../../features/automations/useAutomationFieldsContext.js";
import type { AutosaveStatus } from "../../hooks/useAutosaveDraft.js";

export interface AgentDetailOutletContext {
  automation: Automation;
  agentsHref: string;
  /**
   * Registers (or clears, with `null`) the Overview tab's pending-save flusher
   * so the detail page can persist in-flight edits before a preview run. The
   * flusher resolves to `true` once the config is saved, or `false` if it failed.
   */
  registerConfigFlush: (flush: (() => Promise<boolean>) | null) => void;
  /**
   * Lets the Overview tab surface its live autosave state to the header's info
   * popover (the save indicator now lives there).
   */
  reportSaveStatus: (
    status: AutosaveStatus,
    lastSavedAt: string | null,
  ) => void;
}

export function useAgentDetailOutletContext(): AgentDetailOutletContext {
  return useOutletContext<AgentDetailOutletContext>();
}

/**
 * Layout for a saved agent: breadcrumb, header (delete / run preview /
 * enable), and the Overview | Results | Runs tabs rendered via the outlet.
 */
export const AgentDetailPage: FC = () => {
  const { playbookId, automationId } = useParams();

  const parsedAutomationId = automationId
    ? automationIdSchema.safeParse(automationId)
    : undefined;

  const { data: automation, isLoading } = useAutomationByIdQuery(
    parsedAutomationId?.success ? parsedAutomationId.data : skipToken,
  );

  if (!parsedAutomationId?.success) {
    return <Navigate to=".." replace />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loader style={LoaderStyle.ZOOMIES} />
      </div>
    );
  }

  if (!automation) {
    return <Navigate to=".." replace />;
  }

  return (
    <AgentDetailPageContent
      key={automation.id}
      automation={automation}
      agentsHref={
        playbookId ? appPaths.playbookAgents.build({ playbookId }) : ".."
      }
    />
  );
};

const AgentDetailPageContent: FC<{
  automation: Automation;
  agentsHref: string;
}> = ({ automation, agentsHref }) => {
  const navigate = useNavigate();

  // The coaching template is a thin configuration shell over the existing
  // playbook coaching feature: it produces no automation runs, so we hide the
  // Overview/Results/Runs tab strip (showing the config directly) and the
  // preview run.
  const isCoachingTemplate =
    automation.template_id === AutomationTemplateIds.TEAMMATE_COACHING;

  const [updateAutomation] = useUpdateAutomationMutation();
  const [deleteAutomation] = useDeleteAutomationMutation();
  const [createPreviewRun, { isLoading: isPreviewRunStarting }] =
    useCreatePreviewAutomationRunMutation();
  const { playbookNamesById, isReadOnly } = useAutomationFieldsContext();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // The Overview tab registers its pending-save flusher here so we can persist
  // in-flight config edits before starting a preview run.
  const configFlushRef = useRef<(() => Promise<boolean>) | null>(null);
  const registerConfigFlush = useCallback(
    (flush: (() => Promise<boolean>) | null) => {
      configFlushRef.current = flush;
    },
    [],
  );

  // Live autosave state reported by the Overview tab, shown in the info popover.
  const [saveStatus, setSaveStatus] = useState<AutosaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const reportSaveStatus = useCallback(
    (status: AutosaveStatus, savedAt: string | null) => {
      setSaveStatus(status);
      setLastSavedAt(savedAt);
    },
    [],
  );

  const playbookName =
    getAutomationPlaybookIds(automation.settings)
      .map((playbookId) => playbookNamesById.get(playbookId))
      .filter(Boolean)
      .join(", ") || undefined;

  const { data: creator } = useBasicTeammateQuery(
    automation.created_by_teammate_id ?? skipToken,
  );
  const createdByName = creator
    ? renderTeammateDisplayName(creator)
    : undefined;

  const onToggleEnabled = async (isEnabled: boolean) => {
    await updateAutomation({
      id: automation.id,
      is_enabled: isEnabled,
    });
  };

  const onRunPreview = async () => {
    // Persist any pending config edits first so the run pins the latest revision.
    // If they couldn't be saved, don't start a run against stale configuration.
    const didSave = await configFlushRef.current?.();
    if (didSave === false) {
      return;
    }
    try {
      const run = await createPreviewRun(automation.id).unwrap();
      navigate(`runs/${encodeURIComponent(run.id)}`);
    } catch {
      // Starting the preview run failed; keep the user on the detail page.
    }
  };

  const onDelete = async () => {
    try {
      await deleteAutomation(automation.id).unwrap();
      navigate(agentsHref);
    } catch {
      // Deletion failed; keep the user on the detail page.
    }
  };

  return (
    <PageLayout>
      <PageHeader
        eyebrow={playbookName}
        title={automation.name}
        actions={
          <AutomationDetailActions
            automation={automation}
            saveStatus={saveStatus}
            lastSavedAt={lastSavedAt}
            createdByName={createdByName}
            isReadOnly={isReadOnly}
            onToggleEnabled={(isEnabled) => {
              void onToggleEnabled(isEnabled);
            }}
            onDeleteRequest={() => {
              setIsDeleteModalOpen(true);
            }}
            onRunPreview={
              isCoachingTemplate ? undefined : () => void onRunPreview()
            }
            isRunPreviewLoading={isPreviewRunStarting}
          />
        }
        tabs={
          isCoachingTemplate ? undefined : (
            <RouteTabs
              ariaLabel="Agent sections"
              tabs={[
                { label: "Overview", to: "." },
                { label: "Results", to: "results" },
                { label: "Runs", to: "runs" },
              ]}
            />
          )
        }
      />

      <PageBody>
        <AutomationTemplateDescription templateId={automation.template_id} />

        <Outlet
          context={
            {
              automation,
              agentsHref,
              registerConfigFlush,
              reportSaveStatus,
            } satisfies AgentDetailOutletContext
          }
        />
      </PageBody>

      <DeleteAutomationModal
        isOpen={isDeleteModalOpen}
        templateId={automation.template_id}
        onCancel={() => {
          setIsDeleteModalOpen(false);
        }}
        onDelete={() => {
          setIsDeleteModalOpen(false);
          void onDelete();
        }}
      />
    </PageLayout>
  );
};
